import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-0d60c3c4"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "panel" }

import { computed, ref, inject, watch, PropType } from "vue";
import modalBase from "@/components/ui/modals/modal-base.vue";
import { ModalWidth } from "@/types/ui/modals/ModalWidth";
import { DxToolbarItem } from "devextreme-vue/popup";
import { AcquaintApiClient } from "@/services/AcquaintApiClient";
import displayErrorMessage from "@/utils/ErrorHandling";
import { DxTabPanel, DxItem } from "devextreme-vue/tab-panel";
import NumberDropdown from "../ui/dropdowns/number-dropdown.vue";
import { getErrorMessage } from "bright-logic-core/src/errors";
import { EmailNewsletterItems } from "acquaint-api/models/EmailNewsletterItems";
import AcquaintInfoBox from "../ui/inputs/acquaint-info-box.vue";
import NewsConfigurationModal from "../website-news/news-configuration-modal.vue";
import { Newsletter } from "@/types/emails/Newsletter";


export default /*#__PURE__*/_defineComponent({
  __name: 'newsletter-article-modal',
  props: {
  /**
   * The boolean value used with `v-model` to control the visibility or state of a modal or component.
   * @type {boolean}
   * @required
   */
  modelValue: {
    type: Boolean,
    required: true,
  },
  /**
   * The total number of newsletters.
   * @type {number}
   * @default 0
   */
  totalNewsletters: {
    type: Number,
    default: 0,
  },

  /**
   * The unique identifier for the email.
   * Used to fetch or reference a specific email item.
   * @type {number}
   * @required
   */
  emailId: {
    type: Number,
    required: true,
  },

  /**
   * The selected newsletter item.
   * Contains details about the newsletter currently being interacted with.
   * @type {Newsletter}
   * @required
   */
  selectedNewsletterItem: {
    type: Object as PropType<Newsletter>,
    required: true,
  },

  /**
   * Represents an action key related to the newsletter item.
   * Used for tracking specific actions performed on the newsletter.
   * @type {string}
   * @default ''
   */
  actionKey: {
    type: String,
    default: "",
  },
},
  emits: [
  /**
   * Triggered when the `modelValue` changes. This is used to update the v-model variable in the parent component.
   * @property {boolean} value - Indicates whether the modal has been opened or closed.
   */
  "update:modelValue",
  /**
   * Emitted to notify the parent component that data needs to be refreshed.
   * Typically triggered after updates were made.
   */
  "refreshNewsletterData",
],
  setup(__props, { emit }) {

const props = __props;



const selectedItemDescription = ref("");
const displayNewsConfigurationModal = ref(false);
const maxOrderNumber = ref(0);
const emailNewsletterItem = ref<EmailNewsletterItems>({
  emailID: 0,
  newsID: 0,
  displayOrder: 0,
});


const isNewItem = computed(() => props.actionKey == "new");
const dropdownIcon = computed(() => {
  return props.actionKey == "new" ? "fa-sharp fa-solid fa-caret-down" : "";
});
const showModal = computed({
  get: () => props.modelValue,
  set: (value: Boolean) => emit("update:modelValue", value),
});

const acquaintApiClient = inject("AcquaintApiClient") as AcquaintApiClient;

const lockEmailNewsletterItem = async () => {
  try {
    await acquaintApiClient.httpPostVoid(
      `database/EmailNewsletterItems/${props.emailId}/${props.selectedNewsletterItem.newsId}/lock`,
      { lock: true, extraInfo: "" },
    );
    return true;
  } catch (err) {
    displayErrorMessage(getErrorMessage(err));
    return false;
  }
};

const unlockNewsletterItem = async () => {
  try {
    await acquaintApiClient.httpPostVoid(
      `database/EmailNewsletterItems/${props.emailId}/${props.selectedNewsletterItem.newsId}/lock`,
      { lock: false, extraInfo: "" },
    );
  } catch (err) {
    displayErrorMessage(getErrorMessage(err));
  }
};

const fetchNewsletterItem = async () => {
  try {
    if (isNewItem.value) {
      emailNewsletterItem.value =
        await acquaintApiClient.httpGet<EmailNewsletterItems>(
          `database/EmailNewsletterItems/defaultmodel`,
        );
      emailNewsletterItem.value.displayOrder = maxOrderNumber.value;
    } else {
      emailNewsletterItem.value =
        await acquaintApiClient.httpGet<EmailNewsletterItems>(
          `database/EmailNewsletterItems/${props.emailId}/${props.selectedNewsletterItem.newsId}`,
        );
    }
  } catch (err) {
    displayErrorMessage(getErrorMessage(err));
  }
};

watch(
  () => showModal.value,
  async () => {
    if (!showModal.value) {
      return;
    }
    maxOrderNumber.value = props.totalNewsletters ? props.totalNewsletters : 0;
    if (isNewItem.value) {
      selectedItemDescription.value = "";
      maxOrderNumber.value = maxOrderNumber.value + 1;
    } else {
      selectedItemDescription.value = props.selectedNewsletterItem.subject;
    }

    await fetchNewsletterItem();
  },
);

const cancelClick = async () => {
  await unlockNewsletterItem();
  showModal.value = false;
};

const modalClosed = async () => {
  await unlockNewsletterItem();
  showModal.value = false;
};
const saveDetails = async () => {
  if (await callSaveApi()) {
    await unlockNewsletterItem();
    showModal.value = false;
  }
};

const callSaveApi = async () => {
  var result = false;
  switch (props.actionKey) {
    case "new":
      result = await callCreateEmailNewsletterItemApi();
      break;
    case "edit":
      result = await callEditEmailNewsletterApi();
      break;
  }
  return result;
};
const callCreateEmailNewsletterItemApi = async () => {
  try {
    emailNewsletterItem.value.emailID = props.emailId;
    emailNewsletterItem.value =
      await acquaintApiClient.httpPost<EmailNewsletterItems>(
        `database/EmailNewsletterItems`,
        emailNewsletterItem.value,
      );
    emit("refreshNewsletterData");
    return true;
  } catch (err) {
    displayErrorMessage(getErrorMessage(err));
    return false;
  }
};

const callEditEmailNewsletterApi = async () => {
  try {
    if (!(await lockEmailNewsletterItem())) {
      return false;
    }

    emailNewsletterItem.value =
      await acquaintApiClient.httpPut<EmailNewsletterItems>(
        `database/EmailNewsletterItems/${props.emailId}/${props.selectedNewsletterItem.newsId}`,
        emailNewsletterItem.value,
      );
    emit("refreshNewsletterData");
    return true;
  } catch (err) {
    displayErrorMessage(getErrorMessage(err));
    return false;
  }
};

const onConfigurationSelected = (newsId: number, description: string) => {
  selectedItemDescription.value = description;
  emailNewsletterItem.value.emailID = props.emailId;
  emailNewsletterItem.value.displayOrder = maxOrderNumber.value;
  emailNewsletterItem.value.newsID = newsId;
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(modalBase, {
      title: "Newsletter Article",
      modelValue: showModal.value,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((showModal).value = $event)),
      showCloseButton: true,
      height: "300px",
      onModalClosed: modalClosed,
      width: _unref(ModalWidth).Small
    }, {
      content: _withCtx(() => [
        (emailNewsletterItem.value)
          ? (_openBlock(), _createBlock(_unref(DxTabPanel), {
              key: 0,
              "scroll-by-content": true,
              "show-nav-buttons": true,
              "swipe-enabled": false
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(DxItem), { title: "General Details" }, {
                  default: _withCtx(() => [
                    _createElementVNode("section", _hoisted_1, [
                      _createVNode(AcquaintInfoBox, {
                        text: selectedItemDescription.value,
                        label: "News Article",
                        icon: dropdownIcon.value,
                        onIconClicked: _cache[0] || (_cache[0] = 
                () => {
                  displayNewsConfigurationModal.value = true;
                }
              )
                      }, null, 8, ["text", "icon"]),
                      _createVNode(NumberDropdown, {
                        label: "Order",
                        itemNumber: maxOrderNumber.value,
                        "allow-zero-value": false,
                        modelValue: emailNewsletterItem.value.displayOrder,
                        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((emailNewsletterItem.value.displayOrder) = $event))
                      }, null, 8, ["itemNumber", "modelValue"])
                    ])
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }))
          : _createCommentVNode("", true)
      ]),
      footer: _withCtx(() => [
        _createVNode(_unref(DxToolbarItem), {
          widget: "dxButton",
          toolbar: "bottom",
          location: "after",
          options: {
          text: 'Save',
          onClick: () => {
            saveDetails();
          },
        }
        }, null, 8, ["options"]),
        _createVNode(_unref(DxToolbarItem), {
          widget: "dxButton",
          toolbar: "bottom",
          location: "after",
          options: {
          text: 'Cancel',
          onClick: () => {
            cancelClick();
          },
        }
        }, null, 8, ["options"])
      ]),
      _: 1
    }, 8, ["modelValue", "width"]),
    _createVNode(NewsConfigurationModal, {
      modelValue: displayNewsConfigurationModal.value,
      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((displayNewsConfigurationModal).value = $event)),
      "show-select-button": true,
      onConfigurationSelected: onConfigurationSelected
    }, null, 8, ["modelValue"])
  ], 64))
}
}

})